import { notEmpty, stringUtils } from '@festina-life/core'
import { type Key, type ReactNode, useCallback, useMemo, useState } from 'react'

import { isStrictServerDateString } from '../../forms/select/Select.utils'
import {
  type OptionWithRenderedValue,
  useResolvedRenderedValues,
} from '../../forms/select/useResolvedRenderedValues'
import { useStableObjectKey } from '../../hooks/useStableObjectKey'
import type { Column, ColumnId } from './createColumnDefiner'
import { useComparator } from './useComparator'
import { containsReactElement } from './utils'

type Options<T> = {
  columns: Record<ColumnId, Column<T>>
  rows: Required<T[]>
  shouldSkipLocalSorting?: boolean
}

type SortingState<T> = {
  extractionMirrors: ReactNode
  cycleColumnSorting: (options: {
    columnId: ColumnId
    isAdditive: boolean
  }) => void
  getSortedRows: (rows: T[]) => T[]
  getSortingState: (columnId: ColumnId) => ColumnSortingState | null
}

export type ColumnSortingState = {
  isDescending: boolean
  /** Non-zero. Lower values sort first. `null` indicates priorities are not relevant as there is only one item being sorted. */
  priority: number | null
}

type Sorting = {
  columnIds: ColumnId[]
  isDescending: Record<ColumnId, boolean>
}

const createSortKey = (columnId: ColumnId, rowKey: Key) =>
  `${columnId}:${rowKey}`

export const useSortingState = <T extends WeakKey>({
  columns,
  rows,
  shouldSkipLocalSorting = false,
}: Options<T>): SortingState<T> => {
  const compare = useComparator()
  const rowKey = useStableObjectKey<T>()

  const [sorting, setSorting] = useState<Sorting>(() => {
    const initialSortings = Object.entries(columns)
      .flatMap(([columnId, column]) =>
        column.sortOptions
          ? {
              columnId,
              isDescending: column.sortOptions.sortType === 'desc',
              priority: column.sortOptions.sortPriority ?? 0,
            }
          : [],
      )
      .sort((stateA, stateB) => stateA.priority - stateB.priority)

    return {
      columnIds: initialSortings.map(({ columnId }) => columnId),
      isDescending: Object.fromEntries(
        initialSortings.map(({ columnId, isDescending }) => [
          columnId,
          isDescending,
        ]),
      ),
    }
  })

  const activeColumnIds = useMemo(
    () => sorting.columnIds.filter((columnId) => columnId in columns),
    [sorting.columnIds, columns],
  )

  const sortedColumnIds = useMemo(
    () => (shouldSkipLocalSorting ? [] : activeColumnIds),
    [activeColumnIds, shouldSkipLocalSorting],
  )

  const resolvedValuesMap = useMemo(() => {
    const sortValueMap: Record<string, unknown> = {}
    const optionsWithRenderedValues: OptionWithRenderedValue<unknown>[] = []

    if (sortedColumnIds.length === 0) {
      return { sortValueMap, optionsWithRenderedValues }
    }

    for (const row of rows.filter(notEmpty)) {
      const currentRowKey = rowKey(row)

      for (const columnId of sortedColumnIds) {
        const column = columns[columnId]
        const sortValue = column.getSortValue(row)
        const sortKey = createSortKey(columnId, currentRowKey)

        if (containsReactElement(sortValue)) {
          const extractedRenderedValue = stringUtils
            .reactNodeToDisplayText(sortValue)
            .trim()

          sortValueMap[sortKey] = extractedRenderedValue
          optionsWithRenderedValues.push({
            value: sortValue,
            key: sortKey,
            renderedInputValue: sortValue,
            textValue: extractedRenderedValue,
          })
        } else {
          sortValueMap[sortKey] = sortValue
        }
      }
    }

    return {
      sortValueMap,
      optionsWithRenderedValues,
    }
  }, [columns, rows, sortedColumnIds, rowKey])

  const { resolvedRenderedValues, extractionMirrors } =
    useResolvedRenderedValues({
      options: resolvedValuesMap.optionsWithRenderedValues,
      enabled: !shouldSkipLocalSorting,
    })

  const resolveSortValue = useCallback(
    (row: T, columnId: ColumnId) => {
      const sortKey = createSortKey(columnId, rowKey(row))
      const extractedValue = resolvedValuesMap.sortValueMap[sortKey]

      if (!shouldSkipLocalSorting) {
        const thisColumn = columns[columnId]
        const sortValue = thisColumn.getCellValue(row)
        if (
          typeof sortValue === 'string' &&
          isStrictServerDateString(sortValue)
        ) {
          return sortValue
        }
      }

      if (typeof extractedValue === 'string' && extractedValue === '') {
        return resolvedRenderedValues[sortKey]
      }

      return extractedValue
    },
    [
      resolvedValuesMap,
      resolvedRenderedValues,
      rowKey,
      columns,
      shouldSkipLocalSorting,
    ],
  )

  const getSortedRows = useCallback<SortingState<T>['getSortedRows']>(
    (rows) =>
      sortedColumnIds.length === 0
        ? rows
        : rows.toSorted((rowA, rowB) => {
            for (const columnId of sortedColumnIds) {
              const valueA = resolveSortValue(rowA, columnId)
              const valueB = resolveSortValue(rowB, columnId)
              const sortResult = compare(valueA, valueB)
              if (sortResult) {
                return sortResult * (sorting.isDescending[columnId] ? -1 : 1)
              }
            }
            return 0
          }),
    [sortedColumnIds, sorting.isDescending, compare, resolveSortValue],
  )

  const getSortingState = useCallback<SortingState<T>['getSortingState']>(
    (columnId) => {
      const index = activeColumnIds.indexOf(columnId)
      if (index === -1) {
        return null
      }

      return {
        isDescending: sorting.isDescending[columnId],
        priority: activeColumnIds.length === 1 ? null : index + 1,
      }
    },
    [activeColumnIds, sorting.isDescending],
  )

  const cycleColumnSorting = useCallback<SortingState<T>['cycleColumnSorting']>(
    ({ columnId, isAdditive }) => {
      if (!(columnId in columns)) {
        return
      }

      setSorting((sorting) => {
        const activeColumnIds = sorting.columnIds.filter(
          (activeColumnId) => activeColumnId in columns,
        )
        const currentColumnIds =
          activeColumnIds.length === sorting.columnIds.length
            ? sorting.columnIds
            : activeColumnIds
        const isActive = currentColumnIds.includes(columnId)
        const isRemoving = isActive && sorting.isDescending[columnId]
        let columnIds: ColumnId[]

        if (isRemoving) {
          columnIds = isAdditive
            ? currentColumnIds.filter(
                (activeColumnId) => activeColumnId !== columnId,
              )
            : []
        } else if (isActive && (isAdditive || currentColumnIds.length === 1)) {
          columnIds = currentColumnIds
        } else {
          columnIds = isAdditive ? [...currentColumnIds, columnId] : [columnId]
        }

        return {
          columnIds,
          isDescending: Object.fromEntries(
            columnIds.map((activeColumnId) => [
              activeColumnId,
              activeColumnId === columnId
                ? isActive
                : sorting.isDescending[activeColumnId],
            ]),
          ),
        }
      })
    },
    [columns],
  )

  return {
    extractionMirrors,
    getSortedRows,
    getSortingState,
    cycleColumnSorting,
  }
}
